import { Box, Card, TextField, Typography } from "@mui/material"
import { LayoutGrid } from "lucide-react"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { BROCHURE_COLORS, FEATURE_EMOJI_CHOICES, type BrochureFeature, type BrochureSection } from "./stock-images"
import { BROCHURE_FONT, escapeHtml } from "./html-helpers"

// Editor UI for the highlights grid: a section heading + up to three feature
// cards, each an emoji "illustration", a title, and a short blurb. The rendered
// email HTML is produced by `highlightsToHtml`.
interface HighlightsSectionProps {
  section: BrochureSection
  onUpdate: (sectionId: string, partial: any) => void
}

export function HighlightsSection({ section, onUpdate }: HighlightsSectionProps) {
  const c = section.content
  const features: BrochureFeature[] = Array.isArray(c.features) ? c.features : []

  const updateFeature = (id: string, patch: Partial<BrochureFeature>) => {
    onUpdate(section.id, {
      features: features.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    })
  }

  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <LayoutGrid size={20} />
          Highlights
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography component="label" htmlFor={`hl-heading-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Section heading</Typography>
          <TextField
            fullWidth size="small" id={`hl-heading-${section.id}`}
            value={c.heading || ""}
            onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
            placeholder="e.g., Why teams choose us"
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {features.map((f, i) => (
            <Box key={f.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 1 }}>Feature {i + 1}</Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography component="label" sx={{ fontSize: 13, fontWeight: 500, display: "block", mb: 0.5 }}>Icon</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {FEATURE_EMOJI_CHOICES.map((emoji) => (
                    <Box
                      component="button"
                      key={emoji}
                      type="button"
                      onClick={() => updateFeature(f.id, { emoji })}
                      sx={{
                        width: 34, height: 34, fontSize: 18, lineHeight: 1, cursor: "pointer",
                        borderRadius: 1, bgcolor: "background.paper",
                        border: "2px solid",
                        borderColor: f.emoji === emoji ? "primary.main" : "divider",
                      }}
                    >
                      {emoji}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography component="label" sx={{ fontSize: 13, fontWeight: 500 }}>Title</Typography>
                <AiTextImprover fieldType="short" context="brochure feature title" currentText={f.title} onImprove={(v) => updateFeature(f.id, { title: v })} />
              </Box>
              <TextField
                fullWidth size="small" sx={{ mb: 1.5 }}
                value={f.title || ""}
                onChange={(e) => updateFeature(f.id, { title: e.target.value })}
                placeholder="e.g., Always-on reliability"
              />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography component="label" sx={{ fontSize: 13, fontWeight: 500 }}>Description</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <InlineAiGenerator
                    fieldType="long"
                    context="brochure feature description — one concise sentence on the benefit"
                    placeholder="e.g., describe this benefit"
                    currentValue={f.text}
                    onGenerate={(v) => updateFeature(f.id, { text: v })}
                  />
                  <AiTextImprover fieldType="long" context="brochure feature description" currentText={f.text} onImprove={(v) => updateFeature(f.id, { text: v })} />
                </Box>
              </Box>
              <TextField
                fullWidth multiline minRows={2} size="small"
                value={f.text || ""}
                onChange={(e) => updateFeature(f.id, { text: e.target.value })}
                placeholder="Short description"
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Card>
  )
}

// Serialize the highlights grid to email HTML. Only features that have a title
// or text render, and the columns split the width evenly among them (1–3), so
// clearing a card doesn't leave a lopsided grid. On phones the columns stack via
// the `.mobile-stack` class. Emoji glyphs are the "illustrations" (see
// html-helpers.ts). Returns '' when there is nothing to show.
export function highlightsToHtml(section: BrochureSection): string {
  const c = section.content || {}
  const features: BrochureFeature[] = Array.isArray(c.features) ? c.features : []
  const active = features.filter((f) => (f.title || "").trim() || (f.text || "").trim())
  if (active.length === 0) return ""

  const width = Math.floor(100 / active.length)
  const heading = c.heading ? escapeHtml(c.heading) : ""

  const cards = active
    .map(
      (f) => `
                    <td class="mobile-stack" width="${width}%" valign="top" style="width: ${width}%; vertical-align: top; padding: 12px;">
                      <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="${BROCHURE_COLORS.soft}" style="background-color: ${BROCHURE_COLORS.soft}; border-radius: 12px;">
                        <tr>
                          <td align="center" style="padding: 26px 20px; text-align: center;">
                            <div style="font-size: 40px; line-height: 44px; margin-bottom: 12px;">${escapeHtml(f.emoji || "")}</div>
                            ${(f.title || "").trim() ? `<p style="margin: 0 0 8px 0; color: ${BROCHURE_COLORS.navy}; font-family: ${BROCHURE_FONT}; font-size: 18px; font-weight: bold; line-height: 24px;">${escapeHtml(f.title)}</p>` : ''}
                            ${(f.text || "").trim() ? `<p style="margin: 0; color: ${BROCHURE_COLORS.muted}; font-family: ${BROCHURE_FONT}; font-size: 15px; line-height: 22px;">${escapeHtml(f.text)}</p>` : ''}
                          </td>
                        </tr>
                      </table>
                    </td>`,
    )
    .join("")

  return `
          <!-- Highlights -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.card}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.card}; padding: 44px 28px 32px 28px;">
              ${heading ? `<h2 style="margin: 0 0 24px 0; color: ${BROCHURE_COLORS.navy}; font-family: ${BROCHURE_FONT}; font-size: 26px; line-height: 32px; font-weight: bold; text-align: center;">${heading}</h2>` : ''}
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>${cards}
                </tr>
              </table>
            </td>
          </tr>
          `
}
